import React, { useState, useEffect, useRef } from 'react';
import './Scrubber.css';
import { SkipBack, ChevronLeft, Play, Pause, ChevronRight, SkipForward, Eye, EyeOff } from './Icons';

const PLAY_DURATION_MS = 30000;
const TICK_MS = 50;

const SPEED_OPTIONS = [
  { label: '0.05x', value: 0.05 },
  { label: '0.1x', value: 0.1 },
  { label: '0.25x', value: 0.25 },
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '2x', value: 2 },
  { label: '5x', value: 5 },
  { label: '10x', value: 10 },
];

const Scrubber = ({
  head,
  tail,
  onHeadChange,
  onTailChange,
  globalTimeRange,
  showPointMarkers,
  onTogglePointMarkers
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1);
  const animRef = useRef(null);
  const headRef = useRef(head);
  const tailRef = useRef(tail);
  headRef.current = head;
  tailRef.current = tail;

  const hasTimeRange = globalTimeRange.max > globalTimeRange.min;

  // --- Playback animation ---
  useEffect(() => {
    if (!isPlaying || !hasTimeRange) {
      if (animRef.current) {
        clearInterval(animRef.current);
        animRef.current = null;
      }
      return;
    }

    const gap = headRef.current - tailRef.current;

    // If at end, restart from beginning preserving window size
    if (headRef.current >= 1000) {
      const startHead = Math.min(gap, 1000);
      headRef.current = startHead;
      tailRef.current = 0;
      onHeadChange(startHead);
      onTailChange(0);
    }

    const ticksTotal = PLAY_DURATION_MS / TICK_MS;
    const increment = (1000 / ticksTotal) * playSpeed;
    let accum = 0;

    animRef.current = setInterval(() => {
      accum += increment;
      const steps = Math.floor(accum);
      if (steps >= 1) {
        accum -= steps;
        const newHead = Math.min(1000, headRef.current + steps);
        const newTail = gap < 1000 ? Math.max(0, newHead - gap) : 0;
        headRef.current = newHead;
        tailRef.current = newTail;
        onHeadChange(newHead);
        onTailChange(newTail);
        if (newHead >= 1000) {
          setIsPlaying(false);
        }
      }
    }, TICK_MS);

    return () => {
      if (animRef.current) {
        clearInterval(animRef.current);
        animRef.current = null;
      }
    };
  }, [isPlaying, playSpeed, hasTimeRange, onHeadChange, onTailChange]);

  // --- Slider handlers ---
  const handleTailChange = (e) => {
    const v = Math.min(parseInt(e.target.value), head);
    onTailChange(v);
  };

  const handleHeadChange = (e) => {
    const v = Math.max(parseInt(e.target.value), tail);
    onHeadChange(v);
    if (v >= 1000) setIsPlaying(false);
  };

  const handlePlayPause = () => setIsPlaying(prev => !prev);

  const step = (amount) => {
    const gap = head - tail;
    const newHead = Math.max(gap, Math.min(1000, head + amount));
    const newTail = gap < 1000 ? Math.max(0, newHead - gap) : 0;
    onHeadChange(newHead);
    onTailChange(newTail);
  };

  const skipToStart = () => {
    const gap = head - tail;
    onTailChange(0);
    onHeadChange(Math.min(gap, 1000));
  };

  const skipToEnd = () => {
    const gap = head - tail;
    onHeadChange(1000);
    onTailChange(gap < 1000 ? Math.max(0, 1000 - gap) : 0);
    setIsPlaying(false);
  };

  // --- Formatting ---
  const toMs = (sliderVal) => {
    if (!hasTimeRange) return null;
    return globalTimeRange.min + (sliderVal / 1000) * (globalTimeRange.max - globalTimeRange.min);
  };

  const formatTime = (ms) => {
    if (!ms && ms !== 0) return '--:--:--';
    return new Date(ms).toLocaleTimeString('en-US', {
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatDate = (ms) => {
    if (!ms && ms !== 0) return '';
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const headMs = toMs(head);
  const tailMs = toMs(tail);
  const isAtEnd = head >= 1000;
  const isShowAll = head >= 1000 && tail <= 0;
  const hasTailWindow = tail > 0;

  // Highlighted range as percentages
  const rangeLeft = tail / 10;
  const rangeWidth = (head - tail) / 10;

  return (
    <div className="scrubber-container">
      <div className="scrubber-header">
        <span className="scrubber-title">Time Playback</span>
        <button
          className={`point-toggle-btn ${showPointMarkers ? 'active' : ''}`}
          onClick={onTogglePointMarkers}
          title={showPointMarkers ? 'Hide point markers' : 'Show point markers'}
        >
          {showPointMarkers ? <Eye size={14} /> : <EyeOff size={14} />}
          <span>Points</span>
        </button>
      </div>

      <div className="scrubber-controls">
        <div className="slider-row">
          <span className="time-label">{hasTimeRange ? formatTime(globalTimeRange.min) : ''}</span>
          <div className="dual-slider-container">
            <div className="dual-slider-track" />
            <div
              className="dual-slider-range"
              style={{ left: `${rangeLeft}%`, width: `${rangeWidth}%` }}
            />
            <input
              type="range"
              min="0"
              max="1000"
              value={tail}
              onChange={handleTailChange}
              className="dual-slider-input dual-slider-tail"
            />
            <input
              type="range"
              min="0"
              max="1000"
              value={head}
              onChange={handleHeadChange}
              className="dual-slider-input dual-slider-head"
            />
          </div>
          <span className="time-label">{hasTimeRange ? formatTime(globalTimeRange.max) : ''}</span>
        </div>

        <div className="scrubber-bottom-row">
          <div className="scrubber-actions">
            <button
              onClick={skipToStart}
              disabled={head <= (head - tail)}
              className="control-button"
              title="Go to start"
            >
              <SkipBack size={14} />
            </button>
            <button
              onClick={() => step(-10)}
              disabled={tail <= 0 && head <= (head - tail)}
              className="control-button"
              title="Step back"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={handlePlayPause}
              className={`control-button play-button ${isPlaying ? 'playing' : ''}`}
              title={isPlaying ? 'Pause' : 'Play'}
              disabled={!hasTimeRange}
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button
              onClick={() => step(10)}
              disabled={isAtEnd}
              className="control-button"
              title="Step forward"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={skipToEnd}
              disabled={isAtEnd}
              className="control-button"
              title="Show end"
            >
              <SkipForward size={14} />
            </button>
          </div>

          <select
            value={playSpeed}
            onChange={(e) => setPlaySpeed(parseFloat(e.target.value))}
            className="speed-dropdown"
          >
            {SPEED_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className="time-display">
            {!hasTimeRange ? (
              <span className="time-all">No time data</span>
            ) : isShowAll ? (
              <span className="time-all">All points</span>
            ) : hasTailWindow ? (
              <span className="time-current">
                {formatTime(tailMs)} &ndash; {formatTime(headMs)}
              </span>
            ) : (
              <span className="time-current">
                {formatDate(headMs)} {formatTime(headMs)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scrubber;
